import { type components } from '@libraries/typescript/api_client';

export type AgentTestMode = NonNullable<
    components['schemas']['AgentRunCreateRequest']['test_mode']
>;

export type RawToolDescriptionMode = NonNullable<
    components['schemas']['AgentRunCreateRequest']['raw_tool_descriptions']
>;

export type AgentComparison = {
    readonly runId: string;
    readonly role: 'normal' | 'raw';
};

export function resolveAgentComparison({
    comparisonRunId,
    comparisonRole,
}: {
    readonly comparisonRunId: unknown;
    readonly comparisonRole: unknown;
}): AgentComparison | null {
    if (
        typeof comparisonRunId !== 'string' ||
        (comparisonRole !== 'normal' && comparisonRole !== 'raw')
    ) {
        return null;
    }
    return { runId: comparisonRunId, role: comparisonRole };
}

export function resolveAgentTestMode({
    hasSelectedConversation,
    pendingMode,
    rawMode,
    comparisonRunId,
}: {
    readonly hasSelectedConversation: boolean;
    readonly pendingMode: AgentTestMode;
    readonly rawMode: boolean;
    readonly comparisonRunId: string | null;
}): AgentTestMode {
    if (!hasSelectedConversation) {
        return pendingMode;
    }
    if (rawMode) {
        return 'raw';
    }
    if (comparisonRunId !== null) {
        return 'parallel';
    }
    return 'normal';
}

export function resolveAgentTestModeForCreate({
    pendingMode,
    isPlatformAdmin,
    isInternalOrganization,
    isV3Enabled,
    isGeneralRun,
    isAgentTestingSurface,
    allowParallel,
}: {
    readonly pendingMode: AgentTestMode;
    readonly isPlatformAdmin: boolean;
    readonly isInternalOrganization: boolean;
    readonly isV3Enabled: boolean;
    readonly isGeneralRun: boolean;
    readonly isAgentTestingSurface: boolean;
    readonly allowParallel: boolean;
}): AgentTestMode {
    if (
        !isPlatformAdmin ||
        !isInternalOrganization ||
        !isV3Enabled ||
        !isGeneralRun ||
        !isAgentTestingSurface ||
        (pendingMode === 'parallel' && !allowParallel)
    ) {
        return 'normal';
    }
    return pendingMode;
}

export function shouldHideComparisonRun({
    comparisonEnabled,
    comparisonRole,
}: {
    readonly comparisonEnabled: boolean;
    readonly comparisonRole: 'normal' | 'raw' | null | undefined;
}): boolean {
    return (
        !comparisonEnabled &&
        comparisonRole !== null &&
        comparisonRole !== undefined
    );
}

export function shouldMirrorAgentComparisonMessage({
    comparison,
    activityEventIds,
    uploadIds,
    messageOrigin,
    hasWidgetAction,
    hasWidgetDiagnostic,
}: {
    readonly comparison: {
        readonly runId: string;
        readonly role: 'normal' | 'raw';
    } | null;
    readonly activityEventIds: ReadonlyArray<string>;
    readonly uploadIds: ReadonlyArray<string>;
    readonly messageOrigin: string | undefined;
    readonly hasWidgetAction: boolean;
    readonly hasWidgetDiagnostic: boolean;
}): boolean {
    return (
        comparison?.role === 'normal' &&
        activityEventIds.length === 0 &&
        uploadIds.length === 0 &&
        messageOrigin === undefined &&
        !hasWidgetAction &&
        !hasWidgetDiagnostic
    );
}

export function canShowAgentTestModeControl({
    isPlatformAdmin,
    isInternalOrganization,
    isV3Enabled,
    isOutreach,
    hasComparisonRun,
    isAgentTestingSurface,
    allowParallel,
    isGeneralRun,
}: {
    readonly isPlatformAdmin: boolean;
    readonly isInternalOrganization: boolean;
    readonly isV3Enabled: boolean;
    readonly isOutreach: boolean;
    readonly hasComparisonRun: boolean;
    readonly isAgentTestingSurface: boolean;
    readonly allowParallel: boolean;
    readonly isGeneralRun: boolean;
}): boolean {
    return (
        isPlatformAdmin &&
        isInternalOrganization &&
        isV3Enabled &&
        !isOutreach &&
        isAgentTestingSurface &&
        (!hasComparisonRun || allowParallel) &&
        isGeneralRun
    );
}
